import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import {
  ArrowLeft, ChevronRight, ChevronDown, Send, Loader2, Eye, EyeOff, FileText,
  Sparkles, Paperclip, Briefcase, ExternalLink,
  Clock, CheckCircle, CalendarClock, Search, GitCompare,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { applicationService } from '@core/supabase/services/applicationService';
import { interviewEventService, type InterviewEvent } from '@core/supabase/services/interviewEventService';
import { activityService } from '@core/supabase/services/activityService';
import { emailService } from '@core/supabase/services/emailService';
import { maskingService } from '@core/supabase/services/maskingService';
import { optimizationService } from '@core/supabase/services/optimizationService';
import { Application, EducationEntry, EmailThread } from '@core/types';
import {
  TemplateKey, renderTemplate, TEMPLATE_LABELS, TemplateVars,
  MERGE_FIELDS, applyTemplateVarsToText,
} from '@core/email/templates';
import { emailTemplateService, type EmailTemplate } from '@core/supabase/services/emailTemplateService';
import { injectEmailTokens } from '@core/utils/emailTokens';
import { plainToSimpleHtml, stripHtml } from '@core/utils/htmlPlain';
import { invokeLlamaChat } from '@core/ai/llamaChat';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { useToast } from '@shared/hooks/useToast';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { RichTextEditor } from '@shared/components/RichTextEditor';
import { BUILTIN_EMAIL_TEMPLATE_GROUPS } from './emailTemplateCatalog';
import { STARTER_TEMPLATES, STARTER_TEMPLATE_CATEGORIES } from './lib/starterTemplates';

/* ─── Utils ──────────────────────────────────────────────────── */
const BRAND = '#8B80F9';

function formatEducation(education: EducationEntry[] | null | undefined): string {
  if (!education?.length) return '—';
  return education
    .map((e) => (e.year ? `${e.degree} (${e.year})` : e.degree))
    .filter(Boolean)
    .join(', ') || '—';
}

const FOLLOWUP_DELAYS: { label: string; days: number | null }[] = [
  { label: 'No follow-up', days: null },
  { label: 'Follow up in 3 days', days: 3 },
  { label: 'Follow up in 5 days', days: 5 },
  { label: 'Follow up in 7 days', days: 7 },
];

function daysFromNow(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

/** Next upcoming scheduled interview, else latest round (same heuristic as session calendar). */
function pickPrimaryInterviewEvent(events: InterviewEvent[]): InterviewEvent | null {
  if (events.length === 0) return null;
  const now = Date.now();
  const upcoming = events
    .filter((e) => e.scheduled_at && new Date(e.scheduled_at).getTime() >= now)
    .sort((a, b) => new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime())[0];
  return upcoming ?? [...events].sort((a, b) => b.round - a.round)[0] ?? null;
}

export type EmailComposeLocationState = {
  returnTo?: string;
  recipientOverride?: { email: string; name?: string };
  templateSelect?: string;
  initialCustomBody?: string;
};

export type EmailComposeViewProps = {
  applicationId: string | null;
  replyToThreadId?: string;
  /**
   * Full thread being replied to — when provided together with `embedded`, the right
   * panel renders in Outlook reply mode: empty composer at top, separator, quoted
   * original message below.
   */
  replyToThread?: EmailThread;
  /** From router state or parent (recipient overrides, template hints). */
  composeState?: EmailComposeLocationState | null;
  /** Explicit return URL after send / cancel; overrides composeState.returnTo */
  returnTo?: string;
  /** Inline on session pages — same markup as full compose, no full-bleed page chrome */
  embedded?: boolean;
  onSent?: (threadId: string) => void;
  onCancel?: () => void;
  /** Merge into merge-vars (e.g. PREP_URL, SESSION_TYPE_LABEL from AI interview session). */
  templateVarOverrides?: Partial<TemplateVars>;
  /** Default "To" when application loads — client contact vs candidate email. */
  defaultRecipient?: 'client' | 'candidate';
  /** Applied once when application row is ready (e.g. `b:ai_interview_prep_invite_candidate`). */
  initialTemplateSelect?: string;
  /** Pre-loaded org templates from the parent page — avoids stale list on first open. */
  orgTemplates?: EmailTemplate[];
};

/* ─── Component ──────────────────────────────────────────────── */
export function EmailComposeView({
  applicationId: applicationIdProp,
  replyToThreadId: replyToThreadIdProp,
  replyToThread,
  composeState: composeStateProp,
  returnTo: returnToProp,
  embedded = false,
  onSent,
  onCancel,
  templateVarOverrides,
  defaultRecipient = 'client',
  initialTemplateSelect,
  orgTemplates: orgTemplatesProp,
}: EmailComposeViewProps) {
  const [applicationId, setApplicationId] = useState<string | null>(applicationIdProp);
  useEffect(() => { setApplicationId(applicationIdProp); }, [applicationIdProp]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();
  const [searchParams] = useSearchParams();
  const returnToFromQuery = searchParams.get('returnTo');
  const returnTo =
    returnToProp ??
    composeStateProp?.returnTo ??
    (returnToFromQuery
      ? (() => {
          try {
            return decodeURIComponent(returnToFromQuery);
          } catch {
            return returnToFromQuery;
          }
        })()
      : undefined);
  const replyToThreadId = replyToThreadIdProp ?? searchParams.get('replyToThread') ?? undefined;

  /** True when this composer is opened inline as a reply inside the Sent-tab right panel. */
  const isInlineReply = embedded && !!replyToThread;

  /** Editable "Re: …" subject — only active in inline reply mode. */
  const [replySubject, setReplySubject] = useState(() => {
    if (!replyToThread) return '';
    const s = replyToThread.subject ?? '';
    return s.startsWith('Re:') ? s : `Re: ${s}`;
  });

  /** Pre-fill recipient + subject when the original thread changes. */
  useEffect(() => {
    if (!replyToThread) return;
    const s = replyToThread.subject ?? '';
    setReplySubject(s.startsWith('Re:') ? s : `Re: ${s}`);
    setForm((f) => ({
      ...f,
      recipientEmail: replyToThread.recipient_email,
      recipientName: replyToThread.recipient_name ?? '',
      customBody: '',
      followupDays: null,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyToThread?.id]);

  const [application, setApplication] = useState<Application | null>(null);
  const [candidateAlias, setCandidateAlias] = useState<string>('');
  const [loading, setLoading] = useState(!!applicationId);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [orgTemplatesFetched, setOrgTemplatesFetched] = useState<EmailTemplate[]>([]);
  /** If parent provides templates (e.g. EmailPage), prefer those to avoid stale lists.
   *  Otherwise fall back to the locally-fetched copy. */
  const orgTemplates = orgTemplatesProp && orgTemplatesProp.length > 0
    ? orgTemplatesProp
    : orgTemplatesFetched;
  const [interviewEvents, setInterviewEvents] = useState<InterviewEvent[]>([]);
  const [openMenu, setOpenMenu] = useState<null | 'followup' | 'templates' | 'meeting' | 'application'>(null);
  const [appPickerQuery, setAppPickerQuery] = useState('');
  const [templateQuery, setTemplateQuery] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [ccEmail, setCcEmail] = useState('');
  const [bccEmail, setBccEmail] = useState('');
  const toolbarRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (openMenu === null) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (toolbarRootRef.current && !toolbarRootRef.current.contains(target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenu]);

  const [form, setForm] = useState({
    templateSelect: 'b:first_submission' as string,
    recipientEmail: '',
    recipientName: '',
    followupDays: null as number | null,
    customBody: '',
    includeResume: true,
    /** Short client feedback line for candidate outcome templates */
    feedbackSummary: '',
    /** Merge tokens {{INTERVIEW_DATE}} / {{INTERVIEW_LOCATION}} / {{INTERVIEW_ROUND}} — pre-filled from pipeline interviews when available */
    interviewWhenDisplay: '',
    interviewLocationDisplay: '',
    interviewRoundDisplay: '',
  });

  const initialTemplateAppliedRef = useRef(false);
  const interviewSeedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.orgId) return;
    emailTemplateService.list(user.orgId).then(setOrgTemplatesFetched).catch(() => setOrgTemplatesFetched([]));
  }, [user?.orgId]);

  /** Pickable applications — populated for the in-compose "Application" dropdown so users can
   *  pick an application to pre-fill recipient/candidate context without leaving the composer. */
  const [pickableApplications, setPickableApplications] = useState<Application[]>([]);
  useEffect(() => {
    if (!user?.orgId) return;
    applicationService
      .list(user.orgId, { limit: 100 })
      .then(setPickableApplications)
      .catch(() => setPickableApplications([]));
  }, [user?.orgId]);

  const filteredPickableApplications = useMemo(() => {
    const q = appPickerQuery.trim().toLowerCase();
    if (!q) return pickableApplications;
    return pickableApplications.filter((a) => {
      const blob = [a.candidate?.full_name, a.job?.title, a.client?.name, a.id]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [pickableApplications, appPickerQuery]);

  const selectApplicationFromPicker = useCallback((next: string | null) => {
    setApplicationId(next);
    if (!next) setApplication(null);
    setOpenMenu(null);
    setAppPickerQuery('');
  }, []);

  useEffect(() => {
    interviewSeedKeyRef.current = null;
    setForm((f) => ({
      ...f,
      interviewWhenDisplay: '',
      interviewLocationDisplay: '',
      interviewRoundDisplay: '',
    }));
  }, [applicationId]);

  useEffect(() => {
    if (!applicationId || applicationId === 'undefined') {
      setInterviewEvents([]);
      return;
    }
    interviewEventService
      .listByApplication(applicationId)
      .then(setInterviewEvents)
      .catch(() => setInterviewEvents([]));
  }, [applicationId]);

  const primaryInterviewEvent = useMemo(
    () => pickPrimaryInterviewEvent(interviewEvents),
    [interviewEvents]
  );

  useEffect(() => {
    if (!applicationId || !primaryInterviewEvent) return;
    const seedKey = `${applicationId}:${primaryInterviewEvent.id}`;
    if (interviewSeedKeyRef.current === seedKey) return;
    interviewSeedKeyRef.current = seedKey;
    setForm((f) => ({
      ...f,
      interviewWhenDisplay:
        f.interviewWhenDisplay.trim() ||
        (primaryInterviewEvent.scheduled_at ? formatDateTimeGmt(primaryInterviewEvent.scheduled_at) : ''),
      interviewLocationDisplay:
        f.interviewLocationDisplay.trim() ||
        [primaryInterviewEvent.location, primaryInterviewEvent.scheduling_booking_url].filter(Boolean).join(' · ') ||
        '',
      interviewRoundDisplay:
        f.interviewRoundDisplay.trim() || `Round ${primaryInterviewEvent.round}`,
    }));
  }, [applicationId, primaryInterviewEvent]);

  useEffect(() => {
    if (!applicationId || applicationId === 'undefined') return;
    const st = composeStateProp ?? {};
    applicationService.getById(applicationId).then((app) => {
      setApplication(app);
      const qpEmail = searchParams.get('to');
      const qpName = searchParams.get('recipientName');
      const qpTpl = searchParams.get('templateSelect');
      const qpBody = searchParams.get('customBody');
      setForm((f) => {
        let next = { ...f };
        if (st.recipientOverride?.email) {
          next = { ...next, recipientEmail: st.recipientOverride.email, recipientName: st.recipientOverride.name ?? '' };
        } else if (qpEmail) {
          next = { ...next, recipientEmail: qpEmail, recipientName: qpName ?? next.recipientName };
        } else if (defaultRecipient === 'candidate' && app?.candidate?.email) {
          next = {
            ...next,
            recipientEmail: app.candidate.email,
            recipientName: app.candidate.full_name?.trim() || app.candidate.email || '',
          };
        } else if (app?.client?.contact_email) {
          next = { ...next, recipientEmail: app.client.contact_email, recipientName: app.client?.contact_name ?? '' };
        }
        if (st.templateSelect) next = { ...next, templateSelect: st.templateSelect };
        else if (qpTpl) next = { ...next, templateSelect: qpTpl };
        if (st.initialCustomBody) next = { ...next, customBody: st.initialCustomBody };
        else if (qpBody) {
          try { next = { ...next, customBody: decodeURIComponent(qpBody) }; }
          catch { next = { ...next, customBody: qpBody }; }
        }
        return next;
      });
    }).finally(() => setLoading(false));
  }, [applicationId, composeStateProp, searchParams, defaultRecipient]);

  useEffect(() => {
    if (!initialTemplateSelect || !application?.id || initialTemplateAppliedRef.current) return;
    setForm((f) => ({ ...f, templateSelect: initialTemplateSelect }));
    initialTemplateAppliedRef.current = true;
  }, [initialTemplateSelect, application?.id]);

  useEffect(() => {
    if (!application?.candidate_id || !application?.client_id || !user?.orgId) return;
    maskingService.getOrCreateAlias(user.orgId, application.candidate_id, application.client_id)
      .then(setCandidateAlias).catch(() => setCandidateAlias('Candidate'));
  }, [application?.candidate_id, application?.client_id, user?.orgId]);

  const templateVars = useMemo((): TemplateVars => {
    const c = application?.candidate;
    const alias = candidateAlias || 'Candidate';
    const customMsg = form.customBody?.trim();
    const refId = applicationId && application?.client_id
      ? `GGJ-${new Date().getFullYear()}-${String(application.client_id).slice(0, 4).toUpperCase()}-${applicationId.slice(0, 6).toUpperCase()}`
      : undefined;
    const iv = primaryInterviewEvent;
    const interviewDateMerged =
      form.interviewWhenDisplay.trim() ||
      (iv?.scheduled_at ? formatDateTimeGmt(iv.scheduled_at) : '') ||
      templateVarOverrides?.INTERVIEW_DATE ||
      '—';
    const interviewLocationMerged =
      form.interviewLocationDisplay.trim() ||
      [iv?.location, iv?.scheduling_booking_url].filter(Boolean).join(' · ') ||
      templateVarOverrides?.INTERVIEW_LOCATION ||
      '—';
    const interviewRoundMerged =
      form.interviewRoundDisplay.trim() ||
      (iv ? `Round ${iv.round}` : '') ||
      templateVarOverrides?.INTERVIEW_ROUND ||
      '—';

    const base: TemplateVars = {
      CANDIDATE_NAME: alias,
      CANDIDATE_SKILLS: c?.parsed_skills?.length ? c.parsed_skills.join(', ') : undefined,
      CANDIDATE_YEARS_EXP: c?.years_exp != null ? `${c.years_exp} years` : undefined,
      CANDIDATE_CURRENT_ROLE: c?.current_title ?? undefined,
      CANDIDATE_EDUCATION: c?.education ? formatEducation(c.education) : undefined,
      CUSTOM_MESSAGE: customMsg
        ? `<div style="color:#e4e4e7;font-size:14px;line-height:1.7;margin:0 0 16px;">${customMsg}</div>`
        : undefined,
      JOB_TITLE: application?.job?.title ?? 'Job Title',
      CLIENT_NAME: form.recipientName || application?.client?.name || 'Client',
      EMPLOYER_NAME: application?.client?.name ?? undefined,
      CLIENT_FEEDBACK_SUMMARY: form.feedbackSummary?.trim() || undefined,
      RECRUITER_NAME: user?.name ?? 'Recruiter',
      ORG_NAME: user?.orgName ?? 'Organisation',
      REF_ID: refId,
      INTERVIEW_DATE: interviewDateMerged,
      INTERVIEW_LOCATION: interviewLocationMerged,
      INTERVIEW_ROUND: interviewRoundMerged,
    };
    return { ...base, ...templateVarOverrides, INTERVIEW_DATE: interviewDateMerged, INTERVIEW_LOCATION: interviewLocationMerged, INTERVIEW_ROUND: interviewRoundMerged };
  }, [
    application,
    candidateAlias,
    applicationId,
    form.customBody,
    form.recipientName,
    form.feedbackSummary,
    form.interviewWhenDisplay,
    form.interviewLocationDisplay,
    form.interviewRoundDisplay,
    primaryInterviewEvent,
    user,
    templateVarOverrides,
  ]);

  const { subject, bodyHtml } = useMemo(() => {
    const sel = form.templateSelect;
    if (sel.startsWith('org:')) {
      const id = sel.slice(4);
      const ot = orgTemplates.find((t) => t.id === id);
      if (!ot) return { subject: '', bodyHtml: '' };
      return { subject: applyTemplateVarsToText(ot.subject, templateVars), bodyHtml: applyTemplateVarsToText(ot.body_html, templateVars) };
    }
    if (sel.startsWith('starter:')) {
      const id = sel.slice(8);
      const st = STARTER_TEMPLATES.find((t) => t.id === id);
      if (!st) return { subject: '', bodyHtml: '' };
      const subject = applyTemplateVarsToText(st.subject, templateVars);
      const bodyHtml = applyTemplateVarsToText(st.body_html.replace(/\n/g, '<br/>'), templateVars);
      return { subject, bodyHtml };
    }
    const key = (sel.startsWith('b:') ? sel.slice(2) : sel) as TemplateKey;
    return renderTemplate(key, templateVars);
  }, [form.templateSelect, orgTemplates, templateVars]);

  const previewHtml = useMemo(() => injectEmailTokens(bodyHtml, 'PREVIEW_CTA_TOKEN', 'PREVIEW_TRACK_TOKEN'), [bodyHtml]);

  /**
   * Prepare rendered template HTML for the light-themed rich-text editor.
   *
   * Strategy:
   * – Extract the CTA table + “open response form” line and replace with a
   *   pre-styled, light-mode CTA card (keeps the same `{{..._URL}}` merge tokens)
   *   so Shortlist/Pass/Query look like real actions, not plain links.
   * – Strip dark email backgrounds and lift near-white / grey text to readable
   *   body colours. Remove runtime markers.
   */
  const cleanForEditor = useCallback((html: string): string => {
    if (!html) return '';

    // Built-in `ctaButtons()` in templates.ts: table of three <a> buttons + help line
    const ctaRe =
      /<table\s+cellpadding="0"\s+cellspacing="0"\s+style="margin-top:24px;"\s*>[\s\S]*?<\/table>\s*<p[^>]*>[\s\S]*?response form[\s\S]*?<\/a>[\s\S]*?<\/p>/i;
    const CTA_PL = '\uE000GGJ_CTA_PLACEHOLDER\uE000';
    const ctaProBlock = `<!-- cta:editor -->
<div style="margin:20px 0;padding:0;border-radius:12px;overflow:hidden;border:1px solid #cbd5e1;background:#f8fafc;box-shadow:0 1px 3px rgba(15,23,42,0.08);">
  <div style="padding:12px 16px;border-bottom:1px solid #e2e8f0;background:linear-gradient(180deg,#ffffff 0%,#f1f5f9 100%);">
    <p style="margin:0;font-size:11px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#64748b;">Client actions</p>
  </div>
  <div style="padding:16px 16px 14px 16px;">
    <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;">
      <a href="{{ACCEPT_URL}}" style="display:inline-block;padding:10px 18px;background:linear-gradient(180deg,#059669 0%,#047857 100%);color:#ffffff !important;font-size:13px;font-weight:600;border-radius:8px;text-decoration:none;box-shadow:0 1px 2px rgba(5,150,105,0.4);">✓ Shortlist</a>
      <a href="{{REJECT_URL}}" style="display:inline-block;padding:10px 18px;background:#ffffff;color:#0f172a !important;font-size:13px;font-weight:600;border-radius:8px;text-decoration:none;border:1px solid #cbd5e1;box-shadow:0 1px 2px rgba(15,23,42,0.06);">✗ Pass</a>
      <a href="{{QUERY_URL}}" style="display:inline-block;padding:10px 18px;background:#ffffff;color:#0f172a !important;font-size:13px;font-weight:600;border-radius:8px;text-decoration:none;border:1px solid #cbd5e1;box-shadow:0 1px 2px rgba(15,23,42,0.06);">? Query</a>
    </div>
    <p style="margin:14px 0 0;padding-top:12px;border-top:1px solid #e2e8f0;font-size:12px;color:#64748b;line-height:1.5;">
      Prefer the full form? <a href="{{CTA_URL}}" style="color:#4f46e5 !important;font-weight:600;text-decoration:underline;">Open response form ↗</a>
    </p>
  </div>
</div>`;

    // Light/near-white colours that would be invisible on white bg
    const LIGHT_COLOR_RE =
      /(color\s*:\s*)(#(?:fff(?:fff)?|f[0-9a-f]{5}|e4e4e7|e[0-9a-f]{5}|d[0-9a-f]{5}|c[0-9a-f]{5}|(?:white|snow|ivory|ghost\s*white|floral\s*white))|rgba?\(\s*2[0-9]{2}\s*,\s*2[0-9]{2}\s*,\s*2[0-9]{2}[^)]*\))/gi;

    // `replace` no-ops if the CTA block is not present (no `test()` — avoids regex lastIndex issues)
    let s = html.replace(ctaRe, CTA_PL);

    s = s
      // strip all backgrounds (dark email outer shell — CTA is protected by placeholder)
      .replace(/\s*background(?:-color)?\s*:\s*[^;"']+;?/gi, '')
      // replace white/near-white text with readable dark
      .replace(LIGHT_COLOR_RE, 'color:#1f2937')
      // mid greys in legacy templates
      .replace(/(color\s*:\s*)(#a1a1aa|#9ca3af|#6b7280|#6d47ff)/gi, 'color:#374151')
      .replace(/\{\{TRACK_PIXEL\}\}/g, '')
      .replace(/\{\{CTA_BUTTON\}\}/g, '');

    if (s.includes(CTA_PL)) {
      s = s.split(CTA_PL).join(ctaProBlock);
    }

    return s;
  }, []);

  /** Seed the editor body with the rendered template HTML whenever the template
   *  selection changes, UNLESS the user has already typed/edited a custom body
   *  OR we are in inline-reply mode (editor starts empty for the user's new text). */
  const lastAppliedTemplateRef = useRef<string>('');
  useEffect(() => {
    if (isInlineReply) return;
    if (lastAppliedTemplateRef.current === form.templateSelect) return;
    lastAppliedTemplateRef.current = form.templateSelect;
    setForm((f) => ({ ...f, customBody: cleanForEditor(bodyHtml) }));
  }, [form.templateSelect, bodyHtml, cleanForEditor, isInlineReply]);

  /** AI inline-improve state — one click rewrites the body, Revert restores. */
  const [aiImproving, setAiImproving] = useState(false);
  const [aiPreviousBody, setAiPreviousBody] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiChangeInfo, setAiChangeInfo] = useState<{
    beforePlain: string;
    afterPlain: string;
    beforeWordCount: number;
    afterWordCount: number;
  } | null>(null);
  const [showAiCompare, setShowAiCompare] = useState(false);

  useEffect(() => {
    setAiPreviousBody(null);
    setAiChangeInfo(null);
    setShowAiCompare(false);
  }, [form.templateSelect]);

  const runAiImprove = useCallback(async () => {
    if (aiImproving) return;
    setAiError(null);
    const currentHtml = form.customBody.trim() ? form.customBody : bodyHtml;
    const currentPlain = stripHtml(currentHtml).trim();
    if (!currentPlain) {
      setAiError('Pick a template or write a draft first.');
      return;
    }
    setAiImproving(true);
    try {
      const ctx = `Subject: ${subject}\nRecipient: ${form.recipientName ? `${form.recipientName} ` : ''}<${form.recipientEmail || '—'}>`;
      const result = await invokeLlamaChat(
        [
          {
            role: 'system',
            content:
              'You improve recruiter email copy. Keep the same intent and any {{merge_tokens}} intact. Output plain text paragraphs only (no markdown fences, no headings). Be warm, clear, and concise.',
          },
          {
            role: 'user',
            content: `${ctx}\n\nRewrite the following email body to improve clarity and tone. Do not invent facts.\n\n"""\n${currentPlain}\n"""`,
          },
        ],
        undefined,
        user?.orgId ? { orgId: user.orgId, feature: 'ai_assist_email_compose_body', orgUserId: user.orgUserId ?? null } : null,
      );
      const improved = (result.content || '').trim();
      if (!improved) {
        setAiError('AI returned no suggestion. Try again.');
        return;
      }
      const snapshotHtml = form.customBody.trim() ? form.customBody : bodyHtml;
      const beforePlain = stripHtml(snapshotHtml).trim();
      const afterPlain = improved;
      const wBefore = beforePlain ? beforePlain.split(/\s+/).filter(Boolean).length : 0;
      const wAfter = afterPlain ? afterPlain.split(/\s+/).filter(Boolean).length : 0;
      setAiChangeInfo({
        beforePlain,
        afterPlain,
        beforeWordCount: wBefore,
        afterWordCount: wAfter,
      });
      setAiPreviousBody(snapshotHtml);
      setForm((f) => ({ ...f, customBody: plainToSimpleHtml(improved) }));
      setShowAiCompare(false);
      const delta = wAfter - wBefore;
      const deltaText = delta === 0 ? 'same word count' : delta > 0 ? `+${delta} words` : `${delta} words`;
      showToast({
        title: 'AI updated the message',
        body: `${wAfter} words now (${deltaText} vs ${wBefore} before). Open “What changed” under the header to see before/after text.`,
        variant: 'info',
      });
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'AI request failed');
    } finally {
      setAiImproving(false);
    }
  }, [aiImproving, form.customBody, bodyHtml, subject, form.recipientName, form.recipientEmail, user?.orgId, user?.orgUserId, showToast]);

  const revertAi = useCallback(() => {
    if (aiPreviousBody === null) return;
    setForm((f) => ({ ...f, customBody: aiPreviousBody }));
    setAiPreviousBody(null);
    setAiChangeInfo(null);
    setShowAiCompare(false);
  }, [aiPreviousBody]);

  const resolvedTemplateKey = useMemo(() => {
    const sel = form.templateSelect;
    if (sel.startsWith('org:')) return 'org_template';
    if (sel.startsWith('starter:')) return sel.slice(8);
    if (sel.startsWith('b:')) return sel.slice(2);
    return sel;
  }, [form.templateSelect]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.recipientEmail) { setError('Recipient email is required'); return; }
    if (!applicationId) { setError('No application selected'); return; }
    if (!user?.orgId) return;
    setSending(true); setError('');
    try {
      const nextFollowup = form.followupDays ? daysFromNow(form.followupDays) : null;
      // Prefer the (possibly-edited) editor content; fall back to rendered template HTML.
      const finalBody = form.customBody.trim() ? form.customBody : bodyHtml;
      let maskedResumePath: string | undefined;
      if (form.includeResume) {
        try {
          const result = await optimizationService.generateMaskedPdf(applicationId);
          maskedResumePath = result.masked_resume_url;
        } catch { /* continue without attachment */ }
      }
      const effectiveSubject = isInlineReply ? replySubject : subject;
      const thread = await emailService.createDraft({
        org_id: user.orgId,
        application_id: applicationId,
        sent_by: user.orgUserId ?? undefined,
        recipient_email: form.recipientEmail,
        recipient_name: form.recipientName || undefined,
        subject: effectiveSubject,
        body_html: finalBody,
        template_key: resolvedTemplateKey,
        next_followup_at: nextFollowup,
        ...(replyToThreadId && { in_reply_to_thread_id: replyToThreadId }),
      });
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ggj_b2b_send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          thread_id: thread.id, to: form.recipientEmail, subject: effectiveSubject, body_html: finalBody,
          ...(maskedResumePath && { masked_resume_path: maskedResumePath }),
        }),
      });
      const result = await res.json();
      if (!res.ok || result.error) throw new Error(result.error ?? 'Failed to send email');
      activityService.log(user.orgId, 'email_thread', 'email_sent', {
        entityId: thread.id, actorId: user.orgUserId ?? undefined,
        payload: { thread_id: thread.id, application_id: applicationId, recipient: form.recipientEmail },
      });
      if (onSent) {
        onSent(thread.id);
      } else {
        navigate(returnTo ? `${returnTo}${returnTo.includes('?') ? '&' : '?'}sent=${thread.id}` : `/emails?sent=${thread.id}`);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return embedded ? (
      <div className="flex justify-center py-16" aria-busy="true">
        <Loader2 className="w-10 h-10 animate-spin vscode-text-tertiary" aria-hidden />
      </div>
    ) : (
      <FullPageSpinner />
    );
  }

  const breadcrumb = application
    ? [
        { label: 'Pipeline', href: '/applications' },
        { label: 'Applications', href: '/applications' },
        { label: [application.candidate?.full_name, application.job?.title].filter(Boolean).join(' — ') || 'Application', href: `/applications/${applicationId}` },
        { label: 'Compose Email' },
      ]
    : [
        { label: 'Pipeline', href: '/applications' },
        { label: 'Email activity', href: '/emails' },
        { label: 'Compose Email' },
      ];

  const appStatus = application?.status;

  const backHref = returnTo || (application && applicationId ? `/applications/${applicationId}` : '/emails');

  const toolbarBtn =
    'inline-flex h-8 items-center gap-1.5 rounded-[6px] border px-2.5 text-[11.5px] transition-colors hover:bg-[var(--vscode-bg-hover)]';
  const toolbarBtnStyle = {
    borderColor: 'var(--vscode-border-primary)',
    color: 'var(--vscode-text-secondary)',
  } as const;

  return (
    <div className={`flex h-full min-h-0 w-full min-w-0 flex-col ${embedded ? '' : 'h-[calc(100vh-5rem)]'}`}>
      {toast}
      {!embedded ? (
        <div
          className="flex shrink-0 items-center justify-between gap-3 border-b px-4 py-2"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <nav className="flex min-w-0 items-center gap-1 text-[11.5px]" aria-label="Compose">
            <Link to="/emails" className="shrink-0 text-[var(--vscode-text-link)] hover:underline">
              Emails
            </Link>
            <ChevronRight className="h-3 w-3 shrink-0 vscode-text-tertiary" aria-hidden />
            {application ? (
              <>
                <Link to={`/applications/${applicationId}`} className="min-w-0 truncate text-[var(--vscode-text-link)] hover:underline">
                  {application.candidate?.full_name} — {application.job?.title}
                </Link>
                <ChevronRight className="h-3 w-3 shrink-0 vscode-text-tertiary" aria-hidden />
              </>
            ) : null}
            <span className="shrink-0 font-semibold vscode-text-primary">Compose</span>
          </nav>
          {onCancel ? (
            <button type="button" onClick={onCancel} className={toolbarBtn} style={toolbarBtnStyle}>
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
          ) : (
            <Link to={backHref} className={toolbarBtn} style={toolbarBtnStyle}>
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Link>
          )}
        </div>
      ) : (
        <div
          className="flex shrink-0 items-center justify-between gap-3 border-b px-3 py-1.5"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <div className="flex min-w-0 items-center gap-2 text-[12px]">
            <span className="shrink-0 font-semibold vscode-text-primary">
              {replyToThreadId ? 'Reply' : 'New Email'}
            </span>
            {application && applicationId ? (
              <>
                <span className="shrink-0 vscode-text-tertiary">·</span>
                <span className="min-w-0 truncate vscode-text-secondary">
                  {application.candidate?.full_name} · {application.job?.title}
                </span>
              </>
            ) : null}
          </div>
          {onCancel ? (
            <button type="button" onClick={onCancel} className={toolbarBtn} style={toolbarBtnStyle}>
              <ArrowLeft className="h-3.5 w-3.5" /> Close
            </button>
          ) : null}
        </div>
      )}

      {/* Warning banners — shown only in full-page / non-inline-reply embedded mode */}
      {replyToThreadId && application && !isInlineReply && (
        <div className="shrink-0 border-b border-blue-800/40 bg-blue-900/10 px-4 py-1.5">
          <p className="text-[11px] text-blue-400">
            Replying to client query — this email will be linked to the conversation thread.
          </p>
        </div>
      )}

      <form onSubmit={handleSend} className="flex min-h-0 flex-1 flex-col">
        {/* ── Top toolbar — Outlook-style actions ── */}
        <div
          ref={toolbarRootRef}
          className="relative flex shrink-0 flex-wrap items-center gap-1.5 border-b px-3 py-2"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          {/* Send */}
          <button
            type="submit"
            disabled={sending || !form.recipientEmail}
            className="inline-flex h-8 items-center gap-1.5 rounded-[6px] px-3 text-[12px] font-semibold text-white transition-opacity disabled:opacity-50"
            style={{ background: '#10B981' }}
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {sending ? 'Sending…' : 'Send'}
          </button>

          <span className="mx-1 h-5 w-px" style={{ background: 'var(--vscode-border-primary)' }} />

          {/* In inline-reply mode only show Follow-up + AI; skip Templates/Application/Meeting */}
          {isInlineReply && (
            <>
              {/* Follow-up */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setOpenMenu((m) => (m === 'followup' ? null : 'followup'))}
                  className={toolbarBtn}
                  style={{
                    ...toolbarBtnStyle,
                    color: form.followupDays ? '#10B981' : 'var(--vscode-text-secondary)',
                    borderColor: form.followupDays
                      ? 'color-mix(in srgb, #10B981 55%, var(--vscode-border-primary))'
                      : 'var(--vscode-border-primary)',
                  }}
                >
                  <Clock className="h-3.5 w-3.5" />
                  {form.followupDays ? `Follow-up in ${form.followupDays}d` : 'Follow-up'}
                  <ChevronDown className="h-3 w-3" />
                </button>
                {openMenu === 'followup' && (
                  <div
                    className="absolute left-0 top-full z-40 mt-1 w-56 rounded-[8px] border p-1 shadow-lg"
                    style={{ background: 'var(--vscode-bg-elevated)', borderColor: 'var(--vscode-border-primary)' }}
                  >
                    {FOLLOWUP_DELAYS.map(({ label, days }) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => { setForm((f) => ({ ...f, followupDays: days })); setOpenMenu(null); }}
                        className="flex w-full items-center justify-between gap-2 rounded-[5px] px-2 py-1.5 text-left text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                        style={{ color: 'var(--vscode-text-primary)' }}
                      >
                        <span>{label}</span>
                        {form.followupDays === days && <CheckCircle className="h-3.5 w-3.5" style={{ color: '#10B981' }} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* AI improve */}
              {user?.orgId && (
                <>
                  <button
                    type="button"
                    onClick={runAiImprove}
                    disabled={aiImproving}
                    className={toolbarBtn}
                    style={toolbarBtnStyle}
                  >
                    {aiImproving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    {aiImproving ? 'Improving…' : 'Improve with AI'}
                  </button>
                  {aiPreviousBody !== null && !aiImproving && (
                    <button
                      type="button"
                      onClick={revertAi}
                      className={toolbarBtn}
                      style={{ ...toolbarBtnStyle, color: '#F59E0B', borderColor: 'color-mix(in srgb, #F59E0B 45%, var(--vscode-border-primary))' }}
                    >
                      <ArrowLeft className="h-3.5 w-3.5" /> Revert
                    </button>
                  )}
                </>
              )}
              <div className="ml-auto flex items-center gap-1.5">
                {error || aiError ? (
                  <span className="text-[11px] text-red-400" role="alert">{error || aiError}</span>
                ) : null}
                <button
                  type="button"
                  onClick={() => setShowPreview((v) => !v)}
                  className={toolbarBtn}
                  style={toolbarBtnStyle}
                >
                  {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  {showPreview ? 'Edit' : 'Preview'}
                </button>
              </div>
            </>
          )}

          {/* Full toolbar — shown in compose (non-reply) mode only */}
          {!isInlineReply && (<>

          {/* Templates dropdown — searchable; shows org templates, starter templates, and workflow built-ins */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setOpenMenu((m) => {
                  if (m === 'templates') return null;
                  setTemplateQuery('');
                  return 'templates';
                });
              }}
              className={toolbarBtn}
              style={toolbarBtnStyle}
              title="Choose email template"
            >
              <FileText className="h-3.5 w-3.5" />
              Templates
              <ChevronDown className="h-3 w-3" />
            </button>
            {openMenu === 'templates' && (() => {
              const q = templateQuery.trim().toLowerCase();
              const pick = (val: string) => {
                setForm((f) => ({ ...f, templateSelect: val }));
                setOpenMenu(null);
                setTemplateQuery('');
              };
              const TemplateRow = ({ val, label, badge }: { val: string; label: string; badge?: string }) => {
                const active = form.templateSelect === val;
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => pick(val)}
                    className="flex w-full items-center justify-between gap-2 rounded-[5px] px-2 py-1.5 text-left text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                    style={{
                      color: 'var(--vscode-text-primary)',
                      background: active ? 'var(--vscode-bg-hover)' : 'transparent',
                    }}
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{label}</span>
                      {badge ? <span className="block truncate text-[10px] vscode-text-tertiary">{badge}</span> : null}
                    </span>
                    {active ? <CheckCircle className="h-3.5 w-3.5 shrink-0" style={{ color: '#10B981' }} /> : null}
                  </button>
                );
              };

              // Org templates — always at top
              const filteredOrg = orgTemplates.filter((t) =>
                !q || [t.name, t.subject].join(' ').toLowerCase().includes(q)
              );

              // Starter templates by category
              const filteredStarters = STARTER_TEMPLATES.filter((t) =>
                !q || [t.name, t.subject, t.category].join(' ').toLowerCase().includes(q)
              );
              const starterByCategory = STARTER_TEMPLATE_CATEGORIES.map((cat) => ({
                cat,
                items: filteredStarters.filter((t) => t.category === cat),
              })).filter((g) => g.items.length > 0);

              // Workflow built-ins
              const filteredBuiltin = BUILTIN_EMAIL_TEMPLATE_GROUPS.map((grp) => ({
                label: grp.label,
                keys: grp.keys.filter((k) =>
                  !q || (TEMPLATE_LABELS[k] ?? k).toLowerCase().includes(q)
                ),
              })).filter((g) => g.keys.length > 0);

              const noResults = filteredOrg.length === 0 && filteredStarters.length === 0 && filteredBuiltin.length === 0;

              return (
                <div
                  className="absolute left-0 top-full z-40 mt-1 flex max-h-[min(480px,70vh)] w-[min(100vw-2rem,22rem)] flex-col overflow-hidden rounded-[8px] border shadow-lg"
                  style={{
                    background: 'var(--vscode-bg-elevated)',
                    borderColor: 'var(--vscode-border-primary)',
                  }}
                >
                  {/* Search */}
                  <div className="shrink-0 border-b p-2" style={{ borderColor: 'var(--vscode-border-primary)' }}>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 vscode-text-tertiary" aria-hidden />
                      <input
                        type="search"
                        value={templateQuery}
                        onChange={(e) => setTemplateQuery(e.target.value)}
                        placeholder="Search templates…"
                        className="h-8 w-full rounded-[6px] border bg-transparent py-1 pl-8 pr-2 text-[12px] outline-none"
                        style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  {/* Scrollable list */}
                  <div className="min-h-0 flex-1 overflow-y-auto p-1">
                    {noResults ? (
                      <p className="py-4 text-center text-[11.5px] vscode-text-tertiary">No templates match your search.</p>
                    ) : null}

                    {/* ── Organisation templates (user-created) ── */}
                    {filteredOrg.length > 0 && (
                      <div className="mb-1">
                        <p className="flex items-center gap-1.5 px-2 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide vscode-text-tertiary">
                          <span>My templates</span>
                          <span className="rounded-full px-1 py-0 text-[9px] font-bold" style={{ background: 'color-mix(in srgb, #10B981 20%, transparent)', color: '#10B981' }}>{filteredOrg.length}</span>
                        </p>
                        {filteredOrg.map((t) => (
                          <TemplateRow key={t.id} val={`org:${t.id}`} label={t.name} badge={t.subject} />
                        ))}
                      </div>
                    )}

                    {/* ── Starter templates by category ── */}
                    {starterByCategory.length > 0 && (
                      <>
                        {(filteredOrg.length > 0) && <div className="my-1 border-t" style={{ borderColor: 'var(--vscode-border-primary)' }} />}
                        {starterByCategory.map(({ cat, items }) => (
                          <div key={cat} className="mb-1">
                            <p className="px-2 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide vscode-text-tertiary">{cat}</p>
                            {items.map((t) => (
                              <TemplateRow key={t.id} val={`starter:${t.id}`} label={t.name} badge={t.subject.slice(0, 60)} />
                            ))}
                          </div>
                        ))}
                      </>
                    )}

                    {/* ── Workflow built-ins ── */}
                    {filteredBuiltin.length > 0 && (
                      <>
                        {(filteredOrg.length > 0 || starterByCategory.length > 0) && <div className="my-1 border-t" style={{ borderColor: 'var(--vscode-border-primary)' }} />}
                        <p className="px-2 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide vscode-text-tertiary">Platform workflows</p>
                        {filteredBuiltin.map((grp) => (
                          <div key={grp.label} className="mb-0.5">
                            <p className="px-2 pb-0 pt-1 text-[9.5px] uppercase tracking-wide" style={{ color: 'var(--vscode-text-tertiary)', opacity: 0.8 }}>{grp.label}</p>
                            {grp.keys.map((k) => (
                              <TemplateRow key={k} val={`b:${k}`} label={TEMPLATE_LABELS[k] ?? k} />
                            ))}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Follow-up dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setOpenMenu((m) => (m === 'followup' ? null : 'followup'))}
              className={toolbarBtn}
              style={{
                ...toolbarBtnStyle,
                color: form.followupDays ? '#10B981' : 'var(--vscode-text-secondary)',
                borderColor: form.followupDays
                  ? 'color-mix(in srgb, #10B981 55%, var(--vscode-border-primary))'
                  : 'var(--vscode-border-primary)',
              }}
              title="Schedule auto follow-up"
            >
              <Clock className="h-3.5 w-3.5" />
              {form.followupDays ? `Follow-up in ${form.followupDays}d` : 'Follow-up'}
              <ChevronDown className="h-3 w-3" />
            </button>
            {openMenu === 'followup' && (
              <div
                className="absolute left-0 top-full z-40 mt-1 w-56 rounded-[8px] border p-1 shadow-lg"
                style={{
                  background: 'var(--vscode-bg-elevated)',
                  borderColor: 'var(--vscode-border-primary)',
                }}
              >
                {FOLLOWUP_DELAYS.map(({ label, days }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => {
                      setForm((f) => ({ ...f, followupDays: days }));
                      setOpenMenu(null);
                    }}
                    className="flex w-full items-center justify-between gap-2 rounded-[5px] px-2 py-1.5 text-left text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                    style={{ color: 'var(--vscode-text-primary)' }}
                  >
                    <span>{label}</span>
                    {form.followupDays === days ? (
                      <CheckCircle className="h-3.5 w-3.5" style={{ color: '#10B981' }} />
                    ) : null}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Schedule meeting popover */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setOpenMenu((m) => (m === 'meeting' ? null : 'meeting'))}
              className={toolbarBtn}
              style={toolbarBtnStyle}
              title="Add interview / meeting details"
            >
              <CalendarClock className="h-3.5 w-3.5" />
              Meeting
              <ChevronDown className="h-3 w-3" />
            </button>
            {openMenu === 'meeting' && (
              <div
                className="absolute left-0 top-full z-40 mt-1 w-80 space-y-2 rounded-[8px] border p-3 shadow-lg"
                style={{
                  background: 'var(--vscode-bg-elevated)',
                  borderColor: 'var(--vscode-border-primary)',
                }}
              >
                <p className="text-[10.5px] font-semibold uppercase tracking-wide vscode-text-tertiary">
                  Interview / Meeting details
                </p>
                <div>
                  <label className="block text-[10.5px] vscode-text-tertiary">Date &amp; time</label>
                  <input
                    className="input mt-0.5 w-full text-[12px]"
                    placeholder={primaryInterviewEvent?.scheduled_at ? formatDateTimeGmt(primaryInterviewEvent.scheduled_at) : 'e.g. 2026-04-10 15:00 GMT+05:30'}
                    value={form.interviewWhenDisplay}
                    onChange={(e) => setForm((f) => ({ ...f, interviewWhenDisplay: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-[10.5px] vscode-text-tertiary">Location / video link</label>
                  <input
                    className="input mt-0.5 w-full text-[12px]"
                    placeholder="Office address or Meet/Zoom link"
                    value={form.interviewLocationDisplay}
                    onChange={(e) => setForm((f) => ({ ...f, interviewLocationDisplay: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-[10.5px] vscode-text-tertiary">Round</label>
                  <input
                    className="input mt-0.5 w-full text-[12px]"
                    placeholder="e.g. Round 1 or Screening"
                    value={form.interviewRoundDisplay}
                    onChange={(e) => setForm((f) => ({ ...f, interviewRoundDisplay: e.target.value }))}
                  />
                </div>
                <p className="text-[10px] vscode-text-tertiary">
                  Replaces <code>{'{{INTERVIEW_DATE}}'}</code>, <code>{'{{INTERVIEW_LOCATION}}'}</code>,{' '}
                  <code>{'{{INTERVIEW_ROUND}}'}</code> in the template.
                </p>
              </div>
            )}
          </div>

          {/* Application — searchable list of rows (no nested select inside the popover) */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setOpenMenu((m) => {
                  if (m === 'application') return null;
                  setAppPickerQuery('');
                  return 'application';
                });
              }}
              className={toolbarBtn}
              style={{
                ...toolbarBtnStyle,
                color: application ? 'var(--vscode-text-primary)' : '#F59E0B',
                borderColor: application
                  ? 'var(--vscode-border-primary)'
                  : 'color-mix(in srgb, #F59E0B 50%, var(--vscode-border-primary))',
              }}
              title="Link to an application"
            >
              <Briefcase className="h-3.5 w-3.5" />
              {application
                ? `${application.candidate?.full_name ?? 'Candidate'} · ${application.job?.title ?? 'Job'}`.slice(0, 40)
                : 'Application'}
              <ChevronDown className="h-3 w-3" />
            </button>
            {openMenu === 'application' && (
              <div
                className="absolute left-0 top-full z-40 mt-1 flex max-h-[min(420px,70vh)] w-[min(100vw-2rem,28rem)] flex-col overflow-hidden rounded-[8px] border shadow-lg"
                style={{
                  background: 'var(--vscode-bg-elevated)',
                  borderColor: 'var(--vscode-border-primary)',
                }}
              >
                <div className="shrink-0 border-b p-2" style={{ borderColor: 'var(--vscode-border-primary)' }}>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 vscode-text-tertiary" aria-hidden />
                    <input
                      type="search"
                      value={appPickerQuery}
                      onChange={(e) => setAppPickerQuery(e.target.value)}
                      placeholder="Search candidate, job, or client…"
                      className="h-8 w-full rounded-[6px] border bg-transparent py-1 pl-8 pr-2 text-[12px] outline-none"
                      style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
                      autoComplete="off"
                    />
                  </div>
                  <p className="mt-1.5 px-0.5 text-[10.5px] vscode-text-tertiary">
                    Tap a row to link this email and auto-fill merge fields.
                  </p>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-1">
                  <button
                    type="button"
                    onClick={() => selectApplicationFromPicker(null)}
                    className="flex w-full items-center justify-between gap-2 rounded-[5px] px-2 py-2 text-left text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                    style={{
                      color: 'var(--vscode-text-primary)',
                      background: !applicationId ? 'var(--vscode-bg-hover)' : 'transparent',
                    }}
                  >
                    <span className="vscode-text-secondary">No application — manual To / subject only</span>
                    {!applicationId ? <CheckCircle className="h-3.5 w-3.5 shrink-0" style={{ color: '#F59E0B' }} /> : null}
                  </button>
                  {filteredPickableApplications.length === 0 ? (
                    <p className="px-2 py-3 text-center text-[11.5px] vscode-text-tertiary">
                      {pickableApplications.length === 0 ? 'No applications in this org yet.' : 'No matches for your search.'}
                    </p>
                  ) : (
                    filteredPickableApplications.map((a) => {
                      const active = applicationId === a.id;
                      const line1 = `${a.candidate?.full_name ?? 'Candidate'} — ${a.job?.title ?? 'Job'}`;
                      const line2 = a.client?.name ?? 'Client';
                      return (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => selectApplicationFromPicker(a.id)}
                          className="flex w-full items-center justify-between gap-2 rounded-[5px] px-2 py-2 text-left text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                          style={{
                            color: 'var(--vscode-text-primary)',
                            background: active ? 'var(--vscode-bg-hover)' : 'transparent',
                          }}
                        >
                          <span className="min-w-0">
                            <span className="block truncate font-medium">{line1}</span>
                            <span className="block truncate text-[11px] vscode-text-tertiary">{line2}</span>
                          </span>
                          {active ? <CheckCircle className="h-3.5 w-3.5 shrink-0" style={{ color: '#10B981' }} /> : null}
                        </button>
                      );
                    })
                  )}
                </div>
                {applicationId && application ? (
                  <div
                    className="shrink-0 border-t p-2 text-[11px]"
                    style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-secondary)' }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="vscode-text-tertiary">Status</span>
                      <span className="font-semibold" style={{ color: BRAND }}>{appStatus ?? '—'}</span>
                    </div>
                    <p className="mt-0.5 truncate vscode-text-secondary">
                      To: {application.client?.contact_email ?? '—'}
                    </p>
                    <div className="mt-1.5">
                      <Link
                        to={`/applications/${application.id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-medium"
                        style={{ color: BRAND }}
                        onClick={() => setOpenMenu(null)}
                      >
                        <ExternalLink className="h-3 w-3" /> Open application
                      </Link>
                    </div>
                  </div>
                ) : applicationId && !application ? (
                  <p className="shrink-0 border-t px-2 py-2 text-[11px] vscode-text-tertiary" style={{ borderColor: 'var(--vscode-border-primary)' }}>
                    Loading application…
                  </p>
                ) : null}
              </div>
            )}
          </div>

          {/* AI assist — in-place rewrite with revert. No modal, no pink chrome. */}
          {user?.orgId && (
            <>
              <button
                type="button"
                onClick={runAiImprove}
                disabled={aiImproving}
                className={toolbarBtn}
                style={toolbarBtnStyle}
                title="Improve the email body with AI (in place)"
              >
                {aiImproving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
                {aiImproving ? 'Improving…' : 'Improve with AI'}
              </button>
              {aiPreviousBody !== null && !aiImproving && (
                <button
                  type="button"
                  onClick={revertAi}
                  className={toolbarBtn}
                  style={{
                    ...toolbarBtnStyle,
                    color: '#F59E0B',
                    borderColor: 'color-mix(in srgb, #F59E0B 45%, var(--vscode-border-primary))',
                  }}
                  title="Undo the AI rewrite and restore the previous body"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Revert
                </button>
              )}
            </>
          )}

          {/* Masked resume toggle */}
          {applicationId && (
            <label
              className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-[6px] border px-2.5 text-[11px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
              style={{
                borderColor: form.includeResume
                  ? 'color-mix(in srgb, #10B981 55%, var(--vscode-border-primary))'
                  : 'var(--vscode-border-primary)',
                color: form.includeResume ? '#10B981' : 'var(--vscode-text-secondary)',
              }}
              title="Attach masked-resume PDF"
            >
              <input
                type="checkbox"
                className="hidden"
                checked={form.includeResume}
                onChange={(e) => setForm((f) => ({ ...f, includeResume: e.target.checked }))}
              />
              <Paperclip className="h-3 w-3" />
              Masked resume
            </label>
          )}

          <div className="ml-auto flex items-center gap-1.5">
            {error || aiError ? (
              <span className="text-[11px] text-red-400" role="alert">{error || aiError}</span>
            ) : null}
            <button
              type="button"
              onClick={() => setShowPreview((v) => !v)}
              className={toolbarBtn}
              style={toolbarBtnStyle}
              title={showPreview ? 'Back to editor' : 'Preview rendered email'}
            >
              {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {showPreview ? 'Edit' : 'Preview'}
            </button>
          </div>

          </>)}
        </div>

        {/* From row */}
        <div
          className="flex shrink-0 items-center gap-2 border-b px-4 py-1.5"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <span className="w-14 shrink-0 text-[11.5px] vscode-text-tertiary">From</span>
          <span className="text-[12.5px] vscode-text-primary">{user?.email ?? 'me'}</span>
        </div>

        {/* To / Cc / Bcc */}
        <div
          className="flex shrink-0 items-center gap-2 border-b px-4 py-1.5"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <span className="w-14 shrink-0 text-[11.5px] vscode-text-tertiary">To</span>
          <input
            className="min-w-0 flex-1 bg-transparent text-[12.5px] outline-none placeholder:vscode-text-tertiary"
            type="email"
            placeholder="hr@client.com"
            value={form.recipientEmail}
            onChange={(e) => setForm((f) => ({ ...f, recipientEmail: e.target.value }))}
            style={{ color: 'var(--vscode-text-primary)' }}
          />
          <input
            className="w-44 shrink-0 rounded-[6px] border bg-transparent px-2 py-1 text-[11.5px] outline-none placeholder:vscode-text-tertiary"
            placeholder="Contact name (optional)"
            value={form.recipientName}
            onChange={(e) => setForm((f) => ({ ...f, recipientName: e.target.value }))}
            style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
          />
          {!showCc && (
            <button type="button" onClick={() => setShowCc(true)} className="shrink-0 text-[11px] vscode-text-tertiary hover:vscode-text-secondary">
              Cc
            </button>
          )}
          {!showBcc && (
            <button type="button" onClick={() => setShowBcc(true)} className="shrink-0 text-[11px] vscode-text-tertiary hover:vscode-text-secondary">
              Bcc
            </button>
          )}
        </div>

        {showCc && (
          <div
            className="flex shrink-0 items-center gap-2 border-b px-4 py-1.5"
            style={{ borderColor: 'var(--vscode-border-primary)' }}
          >
            <span className="w-14 shrink-0 text-[11.5px] vscode-text-tertiary">Cc</span>
            <input
              className="min-w-0 flex-1 bg-transparent text-[12.5px] outline-none placeholder:vscode-text-tertiary"
              placeholder="cc@example.com"
              value={ccEmail}
              onChange={(e) => setCcEmail(e.target.value)}
              style={{ color: 'var(--vscode-text-primary)' }}
            />
          </div>
        )}

        {showBcc && (
          <div
            className="flex shrink-0 items-center gap-2 border-b px-4 py-1.5"
            style={{ borderColor: 'var(--vscode-border-primary)' }}
          >
            <span className="w-14 shrink-0 text-[11.5px] vscode-text-tertiary">Bcc</span>
            <input
              className="min-w-0 flex-1 bg-transparent text-[12.5px] outline-none placeholder:vscode-text-tertiary"
              placeholder="bcc@example.com"
              value={bccEmail}
              onChange={(e) => setBccEmail(e.target.value)}
              style={{ color: 'var(--vscode-text-primary)' }}
            />
          </div>
        )}

        {/* Subject */}
        <div
          className="flex shrink-0 items-center gap-2 border-b px-4 py-1.5"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <span className="w-14 shrink-0 text-[11.5px] vscode-text-tertiary">Subject</span>
          {isInlineReply ? (
            <input
              className="min-w-0 flex-1 bg-transparent text-[13px] font-medium outline-none"
              value={replySubject}
              onChange={(e) => setReplySubject(e.target.value)}
              placeholder="Re: …"
              style={{ color: 'var(--vscode-text-primary)' }}
            />
          ) : (
            <input
              className="min-w-0 flex-1 bg-transparent text-[13px] font-medium outline-none"
              value={subject}
              readOnly
              placeholder="Select a template or customise the body below"
              style={{ color: 'var(--vscode-text-primary)' }}
            />
          )}
        </div>

        {/* Feedback summary — only show for outcome templates */}
        {form.templateSelect.includes('feedback') || form.templateSelect.includes('accept') || form.templateSelect.includes('reject') ? (
          <div
            className="flex shrink-0 items-start gap-2 border-b px-4 py-1.5"
            style={{ borderColor: 'var(--vscode-border-primary)' }}
          >
            <span className="mt-1 w-14 shrink-0 text-[11.5px] vscode-text-tertiary">Feedback</span>
            <textarea
              className="min-h-[28px] min-w-0 flex-1 resize-none bg-transparent text-[12.5px] outline-none placeholder:vscode-text-tertiary"
              placeholder="Short client feedback — merged into outcome templates"
              rows={1}
              value={form.feedbackSummary}
              onChange={(e) => setForm((f) => ({ ...f, feedbackSummary: e.target.value }))}
              style={{ color: 'var(--vscode-text-primary)' }}
            />
          </div>
        ) : null}

        {aiChangeInfo ? (
          <div
            className="shrink-0 border-b px-4 py-2"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              borderLeftWidth: 3,
              borderLeftStyle: 'solid',
              borderLeftColor: '#10B981',
              background: 'color-mix(in srgb, #10B981 7%, var(--vscode-bg-primary))',
            }}
            role="status"
            aria-live="polite"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="flex min-w-0 items-start gap-2">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0" style={{ color: '#10B981' }} aria-hidden />
                <div className="min-w-0">
                  <p className="text-[12.5px] font-semibold vscode-text-primary">AI updated this draft</p>
                  <p className="mt-0.5 text-[11px] leading-snug vscode-text-secondary">
                    {aiChangeInfo.beforeWordCount} → {aiChangeInfo.afterWordCount} words
                    {aiChangeInfo.afterWordCount - aiChangeInfo.beforeWordCount !== 0
                      ? ` (${aiChangeInfo.afterWordCount - aiChangeInfo.beforeWordCount > 0 ? '+' : ''}${aiChangeInfo.afterWordCount - aiChangeInfo.beforeWordCount} vs before)`
                      : ' (length similar)'}
                    . <strong>What changed</strong> shows the full before/after text. Use <strong>Revert</strong> in the toolbar to undo.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowAiCompare((v) => !v)}
                className="inline-flex shrink-0 items-center gap-1 rounded-[6px] border px-2 py-1 text-[11.5px] font-medium"
                style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
              >
                <GitCompare className="h-3.5 w-3.5" aria-hidden />
                {showAiCompare ? 'Hide' : 'What changed'}
              </button>
            </div>
            {showAiCompare ? (
              <div className="mt-2 grid max-h-60 gap-2 overflow-hidden md:grid-cols-2">
                <div
                  className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded border"
                  style={{ borderColor: 'var(--vscode-border-primary)' }}
                >
                  <div
                    className="shrink-0 border-b px-2 py-1 text-[10px] font-semibold uppercase tracking-wide vscode-text-tertiary"
                    style={{ borderColor: 'var(--vscode-border-primary)' }}
                  >
                    Before
                  </div>
                  <pre
                    className="min-h-0 max-h-52 flex-1 overflow-auto whitespace-pre-wrap p-2 font-mono text-[11px] leading-relaxed"
                    style={{ color: 'var(--vscode-text-primary)' }}
                  >
                    {aiChangeInfo.beforePlain || '—'}
                  </pre>
                </div>
                <div
                  className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded border"
                  style={{ borderColor: 'var(--vscode-border-primary)' }}
                >
                  <div
                    className="shrink-0 border-b px-2 py-1 text-[10px] font-semibold uppercase tracking-wide vscode-text-tertiary"
                    style={{ borderColor: 'var(--vscode-border-primary)' }}
                  >
                    After (AI)
                  </div>
                  <pre
                    className="min-h-0 max-h-52 flex-1 overflow-auto whitespace-pre-wrap p-2 font-mono text-[11px] leading-relaxed"
                    style={{ color: 'var(--vscode-text-primary)' }}
                  >
                    {aiChangeInfo.afterPlain || '—'}
                  </pre>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Body — big canvas */}
        <div className={`min-h-0 min-w-0 w-full overflow-auto px-4 py-3 ${isInlineReply ? '' : 'flex-1'}`}>
          {showPreview ? (
            <div
              className="rounded-lg border p-3"
              style={{ borderColor: 'var(--vscode-border-primary)' }}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : (
            <RichTextEditor
              key={isInlineReply ? `reply-${replyToThread?.id}` : form.templateSelect}
              value={form.customBody}
              onChange={(html) => setForm((f) => ({ ...f, customBody: html }))}
              placeholder={isInlineReply ? 'Write your reply…' : 'Pick a template above or write your message here…'}
              minHeight={isInlineReply ? 180 : 420}
            />
          )}
          {!isInlineReply && (
            <details className="mt-3">
              <summary className="cursor-pointer select-none text-[11px] vscode-text-tertiary hover:vscode-text-secondary">
                Available merge fields
              </summary>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {MERGE_FIELDS.map((f) => (
                  <span
                    key={f.token}
                    className="cursor-pointer rounded px-2 py-0.5 font-mono text-[10.5px] hover:opacity-90"
                    style={{
                      backgroundColor: 'var(--brand-accent-bg)',
                      border: '1px solid var(--brand-accent-border)',
                      color: 'var(--vscode-primary)',
                    }}
                    title={f.label + (f.required ? ' (required)' : '')}
                    onClick={() => navigator.clipboard.writeText(f.token).catch(() => {})}
                  >
                    {f.token}
                  </span>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* ── Quoted original message — Outlook-style, inline reply mode only ── */}
        {isInlineReply && replyToThread && (
          <div className="flex min-h-0 flex-1 flex-col border-t" style={{ borderColor: 'var(--vscode-border-primary)' }}>
            {/* Separator header */}
            <div
              className="flex shrink-0 items-center gap-3 px-4 py-2"
              style={{ background: 'var(--vscode-bg-secondary)' }}
            >
              <div className="h-px flex-1" style={{ background: 'var(--vscode-border-primary)' }} />
              <span className="shrink-0 text-[10.5px] font-medium vscode-text-tertiary">Original message</span>
              <div className="h-px flex-1" style={{ background: 'var(--vscode-border-primary)' }} />
            </div>
            {/* Quoted metadata */}
            <div
              className="shrink-0 space-y-0.5 border-b px-4 py-2 text-[11px]"
              style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-secondary)' }}
            >
              <p className="vscode-text-secondary">
                <span className="font-semibold vscode-text-primary">From</span>{'  '}
                {user?.email ?? 'me'}
              </p>
              {replyToThread.sent_at && (
                <p className="vscode-text-secondary">
                  <span className="font-semibold vscode-text-primary">Sent</span>{'  '}
                  {formatDateTimeGmt(replyToThread.sent_at)}
                </p>
              )}
              <p className="vscode-text-secondary">
                <span className="font-semibold vscode-text-primary">To</span>{'  '}
                {replyToThread.recipient_name
                  ? `${replyToThread.recipient_name} <${replyToThread.recipient_email}>`
                  : replyToThread.recipient_email}
              </p>
              <p className="vscode-text-secondary">
                <span className="font-semibold vscode-text-primary">Subject</span>{'  '}
                {replyToThread.subject}
              </p>
            </div>
            {/* Quoted email body */}
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 opacity-75">
              <div
                className={[
                  'text-[12px] leading-[1.6] vscode-text-primary',
                  '[&_*]:!max-w-full',
                  '[&_table]:!w-full [&_table]:!max-w-full [&_table]:!border-collapse',
                  '[&_td]:!max-w-full [&_td]:break-words',
                  '[&_img]:!h-auto [&_img]:!max-w-full',
                  '[&_img[width="1"]]:!hidden [&_img[height="1"]]:!hidden',
                  '[&_pre]:whitespace-pre-wrap [&_pre]:break-words',
                  '[&_a]:text-[var(--vscode-text-link)] [&_a]:underline',
                  '[&_p]:my-1',
                ].join(' ')}
                dangerouslySetInnerHTML={{
                  __html: (replyToThread.body_html ?? '<p>No body.</p>')
                    .replace(/\{\{TRACK_PIXEL\}\}/gi, '')
                    .replace(/<img[^>]*track[^>]*>/gi, '')
                    .replace(/<img[^>]*1x1[^>]*>/gi, ''),
                }}
              />
            </div>
          </div>
        )}
      </form>

    </div>
  );
}

/** Full-page route: `/emails/compose?applicationId=…` — same UI as embedded `EmailComposeView`. */
export function EmailComposePage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const composeState = (location.state as EmailComposeLocationState | null) ?? undefined;
  const applicationId = searchParams.get('applicationId');
  const replyToThreadId = searchParams.get('replyToThread') ?? undefined;

  return (
    <EmailComposeView
      applicationId={applicationId}
      replyToThreadId={replyToThreadId}
      composeState={composeState}
    />
  );
}
